<script setup lang="ts">
import { ApiClientError } from "@shopware/api-client";
import type { BoxLayout, DisplayMode } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import {
  buildUrlPrefix,
  getProductFromPrice,
  getProductRoute,
  getSmallestThumbnailUrl,
} from "@shopware/helpers";
import { getProductName } from "~/components/utilities";
import { getCmsTranslate } from "@shopware/helpers";
import { useElementSize } from "@vueuse/core";
import { defu } from "defu";
import { computed, ref, toRefs, useTemplateRef } from "vue";
import { RouterLink } from "vue-router";
import {
  useAddToCart,
  useCartErrorParamsResolver,
  useCartNotification,
  useNotifications,
  useProductWishlist,
  useUrlResolver,
} from "#imports";
import type { Schemas } from "#shopware";

const { pushSuccess, pushError } = useNotifications();
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    isProductListing?: boolean;
    displayMode?: DisplayMode;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
    isProductListing: false,
  }
);

type Translations = {
  product: {
    addedToWishlist: string;
    removedFromTheWishlist: string;
    reason: string;
    cannotAddToWishlist: string;
    addedToCart: string;
    addToCart: string;
    details: string;
    badges: {
      topseller: string;
    };
  };
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  product: {
    addedToWishlist: "has been added to wishlist.",
    removedFromTheWishlist: "has been removed from wishlist.",
    reason: "Reason",
    cannotAddToWishlist: "cannot be added to wishlist.",
    addedToCart: "has been added to cart.",
    addToCart: "Add to cart",
    details: "Details",
    badges: {
      topseller: "Tip",
    },
  },
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product } = toRefs(props);

const { addToCart, isInCart, count } = useAddToCart(product);

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const isLoading = ref(false);

const toggleWishlistProduct = async () => {
  isLoading.value = true;

  try {
    if (!isInWishlist.value) {
      await addToWishlist();
      pushSuccess(
        `${props.product?.translated.name} ${translations.product.addedToWishlist}`
      );
    } else {
      await removeFromWishlist();
      pushError(
        `${props.product?.translated.name} ${translations.product.removedFromTheWishlist}`
      );
    }
  } catch (error) {
    if (error instanceof ApiClientError) {
      const reason = error.details.errors?.[0]?.detail
        ? `${translations.product.reason}: ${error.details.errors?.[0]?.detail}`
        : "";
      return pushError(
        `${props.product?.translated.name} ${translations.product.cannotAddToWishlist}\n${reason}`,
        {
          timeout: 5000,
        }
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const addToCartLoading = ref(false);
const addToCartProxy = async () => {
  addToCartLoading.value = true;
  await addToCart();
  const errors = getErrorsCodes();
  for (const element of errors) {
    const { messageKey, params } = resolveCartError(element);
    pushError(getCmsTranslate(translations.errors[messageKey], params));
  }

  if (!errors.length)
    pushSuccess(
      `${props.product?.translated.name} ${translations.product.addedToCart}`
    );

  addToCartLoading.value = false;
};

const fromPrice = getProductFromPrice(props.product);
const { getUrlPrefix } = useUrlResolver();

const imageElement = useTemplateRef("imageElement");
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
/**
 * Rounds up the given number to the nearest hundred.
 * If the input number is not provided, returns the default thumbnail size.
 *
 * @param num - The number to be rounded up.
 * @returns The number rounded up to the nearest hundred, or the default thumbnail size if input is falsy.
 */

function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed(() => {
  return `${getSmallestThumbnailUrl(
    product.value?.cover?.media
  )}?&height=${roundUp(height.value)}&fit=cover&width=340&height=340`;
});

const getStarColor = (index) => {
  const fullStars = Math.floor(product.value.ratingAverage); // Get whole number of stars
  const decimalPart = product.value.ratingAverage - fullStars; // Get decimal part

  if (index <= fullStars) {
    return "#7EA081"; // Full Star
  } else if (
    index === fullStars + 1 &&
    decimalPart >= 0.3 &&
    decimalPart <= 0.7
  ) {
    return "url(#half-fill)"; // Half Star (between 0.3 and 0.7)
  } else if (index === fullStars + 1 && decimalPart > 0.7) {
    return "#7EA081"; // Almost full (3.8, 3.9 → next star full)
  } else {
    return "#D3D3D3"; // Empty Star
  }
};
</script>

<template>
  <div class="box-wrap-sty">
    <div class="p-1 md:p-2 mb-2 md:mb-3 relative">
      <figure class="box-prod-img aspect-square">
        <RouterLink
          :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
        >
          <NuxtImg
            ref="imageElement"
            loading="lazy"
            format="webp"
            :src="srcPath"
            :alt="getProductName(product) || ''"
            :class="{
              'w-full h-full': true,
              'object-cover':
                displayMode === 'cover' ||
                (displayMode === 'standard' && layoutType === 'image'),
              'object-contain': displayMode === 'contain',
              'object-scale-down':
                displayMode === 'standard' && layoutType !== 'image',
            }"
            width="340"
            height="340"
            data-testid="product-box-img"
          />
        </RouterLink>
      </figure>
      <div class="img-tags">
        <span v-if="product.markAsTopseller">
          {{ $t("product.badges.topseller") }}
        </span>
        <span v-if="product.isNew"> {{ $t("plubel.product.new") }} </span>
        <span
          v-if="product.calculatedPrice?.listPrice?.percentage"
          class="tag_pink"
          >{{ product.calculatedPrice?.listPrice?.percentage }}%</span
        >
      </div>
      <span
        class="favicon"
        aria-label="Add to wishlist"
        type="button"
        :disabled="isLoading"
        data-testid="product-box-toggle-wishlist-button"
        @click="toggleWishlistProduct"
      >
        <client-only>
          <i v-if="isInWishlist" class="fa-solid fa-heart"></i>
          <i v-else class="fa-regular fa-heart"></i>
        </client-only>
      </span>
    </div>
    <div
      class="px-2 md:px-4 pb-3 relative min-h-[160px] md:min-h-[220px] lg:min-h-[220px]"
    >
      <div
        :class="[
          'rating mb-3 md:mb-4 text-[0] flex',
          { 'h-[18px]': !product.ratingAverage },
        ]"
      >
        <span v-for="index in 5" :key="index">
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="half-fill">
                <stop offset="50%" stop-color="#7EA081" />
                <stop offset="50%" stop-color="#D3D3D3" />
              </linearGradient>
            </defs>
            <path
              d="M3.80934 16L5.17233 10.0842L0.601074 6.10526L6.64017 5.57895L8.9887 0L11.3372 5.57895L17.3763 6.10526L12.8051 10.0842L14.1681 16L8.9887 12.8632L3.80934 16Z"
              :fill="getStarColor(index)"
            />
          </svg>
        </span>
      </div>
      <h4
        class="product_head line-clamp-2 text-ellipsis min-h-[36px] md:min-h-[56px]"
      >
        <RouterLink
          :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
          >{{ getProductName(product) }}</RouterLink
        >
      </h4>
      <div class="tag">
        <label v-for="option in product?.options" :key="option.id">
          {{ option.name }}
        </label>
      </div>
      <div
        class="flex justify-between items-end absolute bottom-2 md:bottom-4 w-full start-0 px-2 md:px-4"
      >
        <p>
          <SwListingProductPrice
            :product="product"
            data-testid="product-box-product-price"
          />
        </p>
        <button
          v-if="!fromPrice"
          type="button"
          class="cart-btn"
          :class="{
            'text-white bg-blue-600 hover:bg-blue-700': !isInCart,
            'text-gray-600 bg-gray-100': isInCart,
            'opacity-50 cursor-not-allowed': !product.available,
          }"
          data-testid="add-to-cart-button"
          :disabled="!product.available"
          @click="addToCartProxy"
        >
          <img
            v-if="!addToCartLoading"
            src="assets/images/cart-plus.svg"
            class="max-w-[50%] inline-block"
            alt="add to cart"
          />
          <div
            v-else
            class="border-0 text-center justify-center w-full md:w-auto my-0 md-m-0 py-2 px-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform transition duration-400"
          >
            <i class="fa-solid fa-spinner animate-spin text-xl"></i>
          </div>
        </button>
        <RouterLink
          v-else
          :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
          class="cart-btn"
        >
          <div
            class="border-0 text-center justify-center w-full md:w-auto my-0 md-m-0 py-2 px-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform transition duration-400"
          >
            <img
              src="/assets/images/external-link.svg"
              alt="external-link"
              class="w-9 h-9"
            />
          </div>
        </RouterLink>
      </div>
    </div>
  </div>
</template>
